import './AddTeamMemberModal.scss';

import FilterListIcon from '@mui/icons-material/FilterList';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DismissIcon from 'assets/images/iPrise/Dismiss.svg';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { TeamUserRoles } from 'pages/iPriseV2/common/enum/TeamUserRoles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';

import StylishLoader from '../../../common/components/StylishLoader';

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  empCode: string;
  teamUserRole: string;
  status: boolean;
  login: {
    id: string;
    email: string;
  };
  isTeamLeader: boolean;
  userAlreadyExists: boolean;
}

interface AddTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  onAddMembers: (data: {
    teamLeaderIds: string[];
    supporterIds: string[];
  }) => void;
  teamId?: string; // Team ID to fetch members for
}

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  onAddMembers,
  onClose,
  open,
  teamId,
}) => {
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openTimestamp, setOpenTimestamp] = useState<number>(0);

  // Update timestamp when modal opens to force refetch
  useEffect(() => {
    if (open) {
      setOpenTimestamp(Date.now());
    }
  }, [open]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('skip', String(page * rowsPerPage));
    params.append('take', String(rowsPerPage));
    params.append('search', debouncedSearch);
    params.append('roleType', TeamUserRoles.TEAM_LEADER);
    if (showActiveOnly) {
      params.append('status', 'true');
    }
    return params.toString();
  }, [page, rowsPerPage, debouncedSearch, showActiveOnly]);

  // Fetch team members from API
  const { data: membersResp, isLoading: isLoadingMembers } = useFetchList<any>(
    [
      'team-members-modal',
      teamId,
      page,
      rowsPerPage,
      debouncedSearch,
      showActiveOnly,
      openTimestamp, // Timestamp changes on every modal open, forcing refetch
    ],
    `users/teams/${teamId || ''}?${queryParams}`,
    {
      enabled: !!teamId && open,
    },
  );

  // Extract members from API response
  const members: TeamMember[] = useMemo(
    () => membersResp?.data || [],
    [membersResp],
  );

  const handleClose = useCallback(() => {
    setSelectedMembers([]);
    setSearchTerm('');
    setPage(0);
    onClose();
  }, [onClose]);

  const handleToggleMember = useCallback((memberId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId],
    );
  }, []);

  const handleToggleAll = useCallback(
    (members: TeamMember[]) => {
      const allMemberIds = members.map((m) => m.id);
      if (selectedMembers.length === members.length) {
        setSelectedMembers([]);
      } else {
        setSelectedMembers(allMemberIds);
      }
    },
    [selectedMembers.length],
  );

  const handleAddMembers = useCallback(async () => {
    if (selectedMembers.length > 0) {
      setIsSubmitting(true);
      // Separate selected members by role
      const teamLeaderIds: string[] = [];
      const supporterIds: string[] = [];

      selectedMembers.forEach((memberId) => {
        const member = members.find((m) => m.id === memberId);
        if (member) {
          if (member.teamUserRole === TeamUserRoles.TEAM_LEADER) {
            teamLeaderIds.push(memberId);
          } else if (member.teamUserRole === TeamUserRoles.SUPPORT) {
            supporterIds.push(memberId);
          }
        }
      });

      try {
        await onAddMembers({ supporterIds, teamLeaderIds });
        // Only close modal on success
        handleClose();
      } catch (error) {
        // On failure, don't close modal
        console.error('Failed to add members:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [selectedMembers, members, onAddMembers, handleClose]);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  // API handles filtering and pagination, so we use members directly
  const totalCount = membersResp?.total || 0;

  const isAllSelected = useMemo(
    () =>
      members.length > 0 &&
      members.every((member) => selectedMembers.includes(member.id)),
    [members, selectedMembers],
  );

  return (
    <Dialog
      className="add-team-member-modal"
      maxWidth="md"
      open={open}
      onClose={handleClose}
      fullWidth>
      <Box className="modal-header">
        <Typography className="modal-title">Add Team Member</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="active-checkbox"
            />
          }
          label="Show Active User Only"
          className="active-control"
        />
        <TextField
          placeholder="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-field"
        />
        <IconButton className="sort-button">
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <Button
          variant="contained"
          className="add-button"
          onClick={handleAddMembers}
          disabled={selectedMembers.length === 0 || isSubmitting}>
          {isSubmitting ? (
            <StylishLoader fullPage={false} className="button" />
          ) : selectedMembers.length > 0 ? (
            `Add Member (${selectedMembers.length})`
          ) : (
            'Add Member'
          )}
        </Button>
        <IconButton className="close-button" onClick={handleClose}>
          <img src={DismissIcon} alt="Close" />
        </IconButton>
      </Box>

      <Box className="modal-content">
        <Box className="table-wrapper">
          <Box className="table-main">
            <TableContainer className="table-container" sx={{ minHeight: 400 }}>
              <Table stickyHeader className="members-table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" className="header-cell">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={
                          selectedMembers.length > 0 && !isAllSelected
                        }
                        onChange={() => handleToggleAll(members)}
                        className="header-checkbox"
                      />
                    </TableCell>
                    <TableCell className="header-cell">Name</TableCell>
                    <TableCell className="header-cell">Email</TableCell>
                    <TableCell className="header-cell">Role</TableCell>
                    <TableCell className="header-cell">Team</TableCell>
                    <TableCell className="header-cell">Status</TableCell>
                    <TableCell align="right" className="header-cell" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingMembers ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        className="body-cell"
                        sx={{ padding: '40px 0' }}>
                        <StylishLoader
                          text="Loading members..."
                          className="table"
                        />
                      </TableCell>
                    </TableRow>
                  ) : members.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        className="body-cell">
                        <Typography
                          sx={{
                            color: '#707070',
                            fontFamily: 'Inter',
                            fontSize: '14px',
                          }}>
                          No members found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    members.map((member) => (
                      <TableRow
                        key={member.id}
                        className="table-row"
                        hover
                        onClick={() => handleToggleMember(member.id)}>
                        <TableCell padding="checkbox" className="body-cell">
                          <Checkbox
                            checked={selectedMembers.includes(member.id)}
                            className="row-checkbox"
                          />
                        </TableCell>
                        <TableCell className="body-cell">
                          {member.firstName} {member.lastName}
                        </TableCell>
                        <TableCell className="body-cell">
                          {member.login?.email}
                        </TableCell>
                        <TableCell className="body-cell">
                          {member.teamUserRole}
                        </TableCell>
                        <TableCell className="body-cell">
                          {member.empCode}
                        </TableCell>
                        <TableCell className="body-cell">
                          <Chip
                            label={member.status ? 'Active' : 'Inactive'}
                            className={`status-chip ${
                              !member.status ? 'inactive' : ''
                            }`}
                          />
                        </TableCell>
                        <TableCell align="right" className="body-cell">
                          <IconButton size="small" className="more-btn">
                            <MoreHorizIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box className="filters-sidebar">
              <FilterListIcon className="filters-icon" />
              <Typography className="filters-text">Filters</Typography>
            </Box>
          </Box>
          <TablePagination
            className="table-pagination"
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[50, 100, 150, 200, 250]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddTeamMemberModal;
